import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Camera, ImagePlus, X, Send,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { uploadsApi, validateFile, isHeavyVideo } from '@/lib/uploadsApi';
import { prepareImagePreview, prepareVideoPreview } from '@/utils/mediaPreview';
import { mediaUploader } from '@/lib/mediaUploader';
import { storiesApi } from '@/lib/storiesApi';
import { useUploadCenter } from '@/contexts/UploadCenterContext';
import { CameraCaptureView } from '@/components/Stories/CameraCaptureView';
import { SegmentsInfoCard } from '@/components/Stories/SegmentsInfoCard';
import { StoryTrimDialog } from '@/components/Stories/StoryTrimDialog';
import { SegmentsPreviewStep } from '@/components/Stories/SegmentsPreviewStep';
import { PreviewMuteToggle } from '@/components/Upload/PreviewMuteToggle';
import {
  STORY_SEGMENT_SECONDS,
  segmentCountFor,
  formatClock,
} from '@/utils/storySegments';
import { toast } from 'sonner';
import { tr } from '@/contexts/LanguageContext';

const IMAGE_DURATION = 5;

/**
 * StoryUploadDialog — picker → preview → publish, now with segmentation UI.
 *
 * Step machine (`step` state):
 *   'idle'      — no file picked yet. Source = 'gallery' | 'camera'.
 *   'preview'   — file accepted, ≤ 60 s → show preview + publish button.
 *                 If > 60 s and the user pressed "Saltar y dividir" before,
 *                 we won't land here again.
 *   'trim'      — file > 60 s OR user tapped "Recortar". Renders
 *                 StoryTrimDialog with timeline + handles.
 *   'segments'  — user accepted "Saltar y dividir en N partes". Shows the
 *                 multi-segment preview + publish UI.
 *   'success'   — IG-style centered checkmark before closing.
 *
 * FRONTEND-ONLY contract (founder-approved, Mar 2026):
 *   · The trim handle window IS captured in state, but the file is NOT
 *     actually cut. The original file is uploaded.
 *   · Multi-segment publish flow simulates N rows of upload progress;
 *     the real backend still only stores ONE story (the original file).
 *   · NO new endpoints, NO ffmpeg, NO schema changes.
 *
 * When the founder approves the visual UX we'll wire the real backend
 * segmentation behind these exact same components.
 */
/**
 * Public component — thin shell that only renders the stateful body when
 * `open` is true. When the user (or parent) closes the dialog, the inner
 * tree unmounts and all state is destroyed. Reopening = fresh mount =
 * no stale state, no "reset on close" useEffect needed. This dodges
 * React 19's `set-state-in-effect` rule and also reduces memory of
 * picked file blobs while the dialog isn't visible.
 */
export const StoryUploadDialog = ({ open, onOpenChange, onPublished, onQueued }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <StoryUploadDialogBody
          onOpenChange={onOpenChange}
          onPublished={onPublished}
          onQueued={onQueued}
        />
      )}
    </Dialog>
  );
};

const StoryUploadDialogBody = ({ onOpenChange, onPublished, onQueued }) => {
  const inputRef = useRef(null);
  const videoProbeRef = useRef(null);
  const navigate = useNavigate();
  const { enqueue } = useUploadCenter();

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [kind, setKind] = useState(null); // 'image' | 'video'
  const [duration, setDuration] = useState(IMAGE_DURATION);
  const [step, setStep] = useState('idle');
  const [source, setSource] = useState('camera');  // Iter 132-K — default to camera (IG behaviour).
  // After a trim, this holds the user's window so the UI footer can
  // surface "Publicar recorte (XX s)" instead of "Publicar". We never
  // actually slice the file in the prototype.
  const [trimWindow, setTrimWindow] = useState(null);

  // Free the object URL when we replace the file or unmount.
  useEffect(() => {
    return () => {
      if (previewUrl) {
        try { URL.revokeObjectURL(previewUrl); } catch { /* noop */ }
      }
    };
  }, [previewUrl]);

  const lastPickedSourceRef = useRef('gallery');

  // ── File acceptance pipeline ───────────────────────────────────────
  // Sprint 5 · P0-2 — preview garantizado: valida decodificación real y
  // convierte HEIC→JPEG (Android) antes de mostrar el paso 'preview'.
  const acceptFile = async (f) => {
    if (!f) return;
    let detectedKind;
    try {
      detectedKind = validateFile(f, { prefer: 'visual' });
    } catch (err) {
      toast.error(err.message);
      return;
    }
    let prepared;
    try {
      prepared = detectedKind === 'image'
        ? await prepareImagePreview(f)
        : await prepareVideoPreview(f);
    } catch (err) {
      toast.error(err?.message || tr("No pudimos preparar este archivo. Elige otro."));
      return;
    }
    if (previewUrl) {
      try { URL.revokeObjectURL(previewUrl); } catch { /* noop */ }
    }
    // Heavy-video heads-up — fires once, no blocking modal, IG-style.
    if (isHeavyVideo(f)) {
      toast.info(tr("Este video es pesado. La subida puede tardar unos minutos."), {
        duration: 6000,
        id: 'story-upload-heavy', // dedupe if user re-picks
      });
    }
    setFile(prepared.file);
    setPreviewUrl(prepared.url);
    setKind(detectedKind);
    setTrimWindow(null);

    if (detectedKind === 'image') {
      setDuration(IMAGE_DURATION);
      setStep('preview');
    } else {
      // Duration is filled in by handleVideoMetadata once the metadata
      // loads. We optimistically jump to 'preview' first.
      setStep('preview');
    }
    lastPickedSourceRef.current = source;
    setSource('gallery');
  };

  const handlePick = (f) => acceptFile(f);

  const handleReplace = () => {
    if (previewUrl) {
      try { URL.revokeObjectURL(previewUrl); } catch { /* noop */ }
    }
    setFile(null);
    setPreviewUrl(null);
    setKind(null);
    setDuration(IMAGE_DURATION);
    setTrimWindow(null);
    setStep('idle');
    if (lastPickedSourceRef.current === 'camera') {
      setSource('camera');
    } else {
      setSource('gallery');
      setTimeout(() => inputRef.current?.click(), 0);
    }
  };

  // The hidden <video> element fires onLoadedMetadata. We read the
  // duration here and decide whether to stay in 'preview' (≤ 60 s) or
  // hop to the 'trim' step (> 60 s, founder-approved auto-trim policy).
  const handleVideoMetadata = (e) => {
    const dur = Math.round(e.target.duration || 0);
    if (!dur) return;
    setDuration(dur);
    if (dur > STORY_SEGMENT_SECONDS) {
      setStep('trim');
    }
  };

  // ── Single-story publish (image or trimmed/short video) ───────────
  // Enqueues the upload into the global UploadCenter and closes the
  // dialog immediately — the user can keep navigating while the actual
  // POST runs in the background. Errors surface in the global banner
  // with a Retry button; no draft is lost.
  const publishSingle = () => {
    if (!file || !kind) return;
    const f = file;
    const k = kind;
    const win = trimWindow;
    const dur = duration;
    const effectiveDuration = win
      ? Math.max(1, Math.round(win.end - win.start))
      : (k === 'image' ? IMAGE_DURATION : Math.min(dur, STORY_SEGMENT_SECONDS));

    enqueue({
      kind: 'story',
      label: tr("Historia"),
      // Iter 144 — Premium FFmpeg pipeline. When the file is a video,
      // the backend (`/api/uploads/media`) runs the H.264 high@4.1
      // transcoder that can hard-trim up to 60 s. We surface that to
      // the user so the spinner reads "Procesando video en alta
      // definición (hasta 60s)…" instead of the generic "Procesando…".
      processingLabel: k === 'video'
        ? tr("Procesando video en alta definición (hasta 60s)…")
        : undefined,
      draft: { file: f, kind: k, trimWindow: win, durationSeconds: effectiveDuration },
      taskFn: async ({ onProgress, onState }) => {
        onState('uploading');
        // Iter 133 — direct-to-bucket upload via presigned URL. Falls
        // back to the legacy multipart endpoint if the cloud bucket is
        // not yet configured (operator hasn't wired AWS keys → 503).
        let upload;
        try {
          upload = await mediaUploader.uploadToCloud(f, 'stories', { onProgress });
        } catch (err) {
          if (err?.status === 503) {
            upload = await uploadsApi.uploadMedia(f, { onProgress });
          } else {
            throw err;
          }
        }
        const mediaUrl = upload?.url;
        if (!mediaUrl) throw new Error(tr("No pudimos subir el archivo."));
        onState('processing');
        const story = await storiesApi.create({
          mediaType: k,
          mediaUrl,
          thumbnailUrl: upload?.thumbnail_url || null,
          durationSeconds: effectiveDuration,
        });
        if (!story) throw new Error(tr("No pudimos publicar tu historia."));
        return story;
      },
      onSuccess: (story) => {
        try { onPublished?.(story); } catch { /* noop */ }
        // Decoupled refresh path — if the parent that wired
        // `onPublished` has already unmounted (user navigated away
        // mid-upload), this event still reaches the next mounted
        // StoriesBar so the rail updates without a manual reload.
        try {
          window.dispatchEvent(new CustomEvent('lineage_stories_updated', {
            detail: { story },
          }));
        } catch { /* noop */ }
      },
    });

    // Destacadas (oct-2026): el padre sabe que hay una subida en curso (abre el editor de la colección).
    try { onQueued?.(); } catch { /* noop */ }
    onOpenChange?.(false);
    if (typeof onPublished !== 'function') navigate('/feed');
  };

  // ── Multi-segment "publish" — FRONTEND-ONLY simulation ───────────
  // The real upload uploads the SINGLE original file (one POST, one row
  // in `stories`). The visual feedback in the UploadCenter banner
  // reflects the single real upload.
  const publishSegments = () => {
    if (!file) return;
    const f = file;
    const dur = duration;
    const segCount = segmentCountFor(dur);

    enqueue({
      kind: 'story',
      label: segCount > 1 ? tr("Historias ({0} partes)", segCount) : tr("Historia"),
      // Iter 144 — premium FFmpeg processing label for segmented
      // video stories (the segmenter still POSTs the original file
      // and the backend transcodes the whole asset once).
      processingLabel: tr("Procesando video en alta definición (hasta 60s)…"),
      draft: { file: f, kind: 'video', durationSeconds: Math.min(dur, STORY_SEGMENT_SECONDS) },
      taskFn: async ({ onProgress, onState }) => {
        onState('uploading');
        // Iter 133 — direct-to-bucket via presigned URL, fallback to
        // legacy multipart on storage-unconfigured (503).
        let upload;
        try {
          upload = await mediaUploader.uploadToCloud(f, 'stories', { onProgress });
        } catch (err) {
          if (err?.status === 503) {
            upload = await uploadsApi.uploadMedia(f, { onProgress });
          } else {
            throw err;
          }
        }
        const mediaUrl = upload?.url;
        if (!mediaUrl) throw new Error(tr("No pudimos subir el archivo."));
        onState('processing');
        const story = await storiesApi.create({
          mediaType: 'video',
          mediaUrl,
          thumbnailUrl: upload?.thumbnail_url || null,
          durationSeconds: Math.min(dur, STORY_SEGMENT_SECONDS),
        });
        if (!story) throw new Error(tr("No pudimos publicar tu historia."));
        return story;
      },
      onSuccess: (story) => {
        try { onPublished?.(story); } catch { /* noop */ }
        try {
          window.dispatchEvent(new CustomEvent('lineage_stories_updated', {
            detail: { story },
          }));
        } catch { /* noop */ }
      },
    });

    // Destacadas (oct-2026): el padre sabe que hay una subida en curso (abre el editor de la colección).
    try { onQueued?.(); } catch { /* noop */ }
    onOpenChange?.(false);
    if (typeof onPublished !== 'function') navigate('/feed');
  };

  const segmentCount = kind === 'video' ? segmentCountFor(duration) : 1;
  const isLongVideo = kind === 'video' && segmentCount > 1;

  // Iter 132-J — IG-style fullscreen capture screen.
  //
  // When the user is still choosing (gallery vs camera) we render an
  // immersive full-viewport experience: camera bleeds edge-to-edge, a
  // floating X sits top-left, and the source switcher moves to the
  // bottom in `PUBLICACIÓN / HISTORIA` rhythm. As soon as a file is
  // accepted we transition to the legacy boxed dialog (better for the
  // small trim / segments controls that still need a max-width).
  const isImmersive = step === 'idle';

  // ──────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────
  return (
    <DialogContent
      className={
        isImmersive
          ? 'fixed inset-0 z-50 m-0 p-0 w-screen h-[100svh] max-w-none rounded-none border-0 bg-black gap-0 flex flex-col overflow-hidden translate-x-0 translate-y-0 left-0 top-0'
          : 'sm:max-w-md bg-card border border-border p-0 overflow-hidden gap-0 max-h-[95dvh] w-[calc(100vw-1rem)] flex flex-col'
      }
      data-testid="story-upload-dialog"
    >
        {isImmersive ? (
          /* Iter 132-J/K — Floating overlay header: X-close on the left
             (IG / Snapchat / TikTok all put it there) + title beside it.
             `safe-area-inset-top` keeps the close button clear of the
             iOS notch / Dynamic Island even when Safari's URL bar is
             collapsed. */
          <DialogHeader className="absolute top-0 inset-x-0 z-40 px-3 pb-2 pr-12 flex-row items-start gap-2 bg-gradient-to-b from-black/80 via-black/40 to-transparent pointer-events-none [padding-top:max(env(safe-area-inset-top),12px)]">
            <button
              type="button"
              onClick={() => onOpenChange?.(false)}
              className="pointer-events-auto -ml-1 p-2 rounded-full text-white hover:bg-white/10 active:bg-white/20 transition-colors flex-shrink-0"
              data-testid="story-upload-close-btn"
              aria-label={tr("Cerrar y volver al feed")}
            >
              <X className="w-6 h-6" strokeWidth={2.4} />
            </button>
            <div className="flex-1 pointer-events-auto min-w-0">
              <DialogTitle className="font-display text-base text-white drop-shadow flex items-center gap-1.5 leading-tight">
                <ImagePlus className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="truncate">{tr("Nueva historia")}</span>
              </DialogTitle>
              <DialogDescription className="text-[11px] text-white/70 drop-shadow leading-tight mt-0.5">{tr("Toca para foto · mantén para grabar (hasta {0}).", formatClock(STORY_SEGMENT_SECONDS))}</DialogDescription>
            </div>
          </DialogHeader>
        ) : (
          <DialogHeader className="px-4 py-3 border-b border-border flex-shrink-0">
            <DialogTitle className="font-display text-base flex items-center gap-2">
              <ImagePlus className="w-4 h-4 text-primary" />{tr("Nueva historia")}</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">{tr("Sube una imagen (5 s) o un video (hasta {0} por historia).", formatClock(STORY_SEGMENT_SECONDS))}</DialogDescription>
          </DialogHeader>
        )}

        {/* Source tabs — IG-style:
            · Idle: overlay at the bottom of the camera screen (after viewfinder).
            · Boxed dialog (legacy): pill row at the top. */}
        {step === 'idle' && !isImmersive && (
          <div
            className="grid grid-cols-2 m-3 mb-0 rounded-full bg-secondary p-1 flex-shrink-0"
            role="tablist"
            aria-label={tr("Origen del contenido")}
            data-testid="story-upload-source-tabs"
          >
            <button
              type="button"
              role="tab"
              aria-selected={source === 'gallery'}
              onClick={() => setSource('gallery')}
              data-testid="story-upload-tab-gallery"
              className={`py-1.5 text-xs font-semibold rounded-full transition-colors ${
                source === 'gallery'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground'
              }`}
            >{tr("Galería")}</button>
            <button
              type="button"
              role="tab"
              aria-selected={source === 'camera'}
              onClick={() => setSource('camera')}
              data-testid="story-upload-tab-camera"
              className={`py-1.5 text-xs font-semibold rounded-full transition-colors inline-flex items-center justify-center gap-1 ${
                source === 'camera'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground'
              }`}
            >
              <Camera className="w-3.5 h-3.5" />{tr("Cámara")}</button>
          </div>
        )}

        {/* IDLE — IG-style fullscreen viewfinder OR legacy boxed picker. */}
        {step === 'idle' && isImmersive && (
          <div className="absolute inset-0 z-10 flex flex-col">
            {/* Viewfinder fills everything between the floating header and the bottom dock. */}
            <div className="flex-1 min-h-0 relative bg-black">
              {source === 'gallery' && (
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  data-testid="story-upload-picker-btn"
                  className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/85 hover:text-white transition-colors"
                >
                  <span className="w-20 h-20 rounded-full bg-primary/15 border-2 border-primary/40 flex items-center justify-center">
                    <Camera className="w-8 h-8 text-primary" />
                  </span>
                  <span className="text-base font-semibold">{tr("Selecciona una foto o video")}</span>
                  <span className="text-xs text-white/60">{tr("JPG · PNG · MP4 · MOV · WEBM · hasta 500 MB")}</span>
                </button>
              )}
              {source === 'camera' && (
                <CameraCaptureView
                  onCapture={acceptFile}
                  onFallback={() => setSource('gallery')}
                  fullscreen
                />
              )}
            </div>

            {/* IG-style bottom dock — `PUBLICACIÓN / HISTORIA` rhythm. */}
            <div
              className="flex-shrink-0 z-30 pb-[max(env(safe-area-inset-bottom),0.5rem)] pt-2 bg-gradient-to-t from-black via-black/85 to-transparent"
              role="tablist"
              aria-label={tr("Origen del contenido")}
              data-testid="story-upload-source-tabs"
            >
              <div className="flex items-center justify-center gap-8 text-[11px] tracking-widest font-semibold uppercase">
                <button
                  type="button"
                  role="tab"
                  aria-selected={source === 'gallery'}
                  onClick={() => setSource('gallery')}
                  data-testid="story-upload-tab-gallery"
                  className={`relative py-2 transition-colors ${
                    source === 'gallery' ? 'text-white' : 'text-white/55 hover:text-white/80'
                  }`}
                >{tr("Galería {0}", source === 'gallery' && (
                    <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
                  ))}</button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={source === 'camera'}
                  onClick={() => setSource('camera')}
                  data-testid="story-upload-tab-camera"
                  className={`relative py-2 transition-colors ${
                    source === 'camera' ? 'text-white' : 'text-white/55 hover:text-white/80'
                  }`}
                >{tr("Historia {0}", source === 'camera' && (
                    <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
                  ))}</button>
              </div>
            </div>

            <input
              ref={inputRef}
              type="file"
              accept="image/*,video/*"
              className="hidden"
              onChange={(e) => handlePick(e.target.files?.[0])}
              data-testid="story-upload-file-input"
            />
          </div>
        )}

        {/* IDLE — legacy boxed dialog removed in Iter 132-J. The
            fullscreen `isImmersive` branch above is now the single
            entry path. Kept the constant scoped so the preview step
            can keep using the boxed dialog. */}

        {/* PREVIEW — single story flow */}
        {step === 'preview' && previewUrl && (
          <>
            {/* Scrollable body — keeps publish footer always visible. */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              {/* If the video has segments, show the info card stacked on top.
                  If the user has trimmed, the card is hidden. */}
              {isLongVideo && !trimWindow && (
                <div className="p-3 pb-0">
                  <SegmentsInfoCard
                    videoUrl={previewUrl}
                    durationSeconds={duration}
                    onAction={() => setStep('trim')}
                  />
                </div>
              )}

              <div className="bg-black aspect-[9/16] relative flex items-center justify-center mt-3">
                {kind === 'image' && (
                  <img
                    src={previewUrl}
                    alt="Vista previa"
                    className="w-full h-full object-contain"
                    data-testid="story-upload-preview-img"
                    onError={() => {
                      toast.error(tr("No pudimos mostrar la imagen. Elige otro archivo."));
                      handleReplace();
                    }}
                  />
                )}
                {kind === 'video' && (
                  <video
                    ref={videoProbeRef}
                    src={previewUrl}
                    autoPlay
                    loop
                    playsInline
                    muted
                    onLoadedMetadata={handleVideoMetadata}
                    onError={() => {
                      toast.error(tr("No pudimos reproducir este video. Usa MP4 (H.264) o elige otro."));
                      handleReplace();
                    }}
                    className="w-full h-full object-contain"
                    data-testid="story-upload-preview-video"
                  />
                )}
                {/* Iter 110 — mute toggle so the barber can hear audio
                    before publishing. Sits to the LEFT of the replace
                    button to avoid overlap. Hidden for image stories. */}
                {kind === 'video' && (
                  <PreviewMuteToggle
                    videoRef={videoProbeRef}
                    testIdSuffix="story-upload"
                    className="absolute top-2 right-12 z-30"
                  />
                )}

                <button
                  type="button"
                  onClick={handleReplace}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/55 backdrop-blur-md border border-white/15 flex items-center justify-center text-white hover:bg-black/75"
                  data-testid="story-upload-replace-btn"
                  aria-label={tr("Cambiar archivo")}
                >
                  <X className="w-4 h-4" />
                </button>

                {/* Trim badge — shown after user accepted a trim window */}
                {trimWindow && (
                  <div
                    className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-primary/90 text-primary-foreground text-[11px] font-bold"
                    data-testid="story-upload-trim-badge"
                  >
                    Recorte · {formatClock(trimWindow.end - trimWindow.start)}
                  </div>
                )}
              </div>
            </div>

            {/* Pinned publish footer — always visible. Safe-area padding
                clears the iPhone home indicator; the dialog itself is now
                capped at 95dvh so this row always sits inside viewport. */}
            <div
              className="flex-shrink-0 z-10 bg-card border-t border-border flex items-center justify-between gap-2 px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
              data-testid="story-upload-publish-footer"
            >
              <p className="text-[11px] text-muted-foreground flex-1 min-w-0 truncate">
                {kind === 'image' && tr("Duración: 5 s")}
                {kind === 'video' && !trimWindow && tr("Duración: {0}", formatClock(duration))}
                {kind === 'video' && trimWindow && (
                  <span className="text-primary">
                    Recorte: {formatClock(trimWindow.end - trimWindow.start)} ·{' '}
                    <button
                      type="button"
                      onClick={() => setStep('trim')}
                      className="underline"
                      data-testid="story-upload-edit-trim-btn"
                    >{tr("Editar")}</button>
                  </span>
                )}
              </p>
              <Button
                onClick={publishSingle}
                disabled={!file}
                data-testid="story-upload-publish-btn"
                className="h-10 px-5 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5 font-semibold flex-shrink-0"
              >
                <Send className="w-4 h-4" />{tr("Publicar")}</Button>
            </div>
          </>
        )}

        {/* TRIM */}
        <AnimatePresence>
          {step === 'trim' && previewUrl && (
            <motion.div
              key="trim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0"
            >
              <StoryTrimDialog
                videoUrl={previewUrl}
                durationSeconds={duration}
                onCancel={() => setStep('preview')}
                onTrim={(win) => { setTrimWindow(win); setStep('preview'); }}
                onSkipAndSplit={() => setStep('segments')}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* SEGMENTS */}
        <AnimatePresence>
          {step === 'segments' && previewUrl && (
            <motion.div
              key="segments"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0"
            >
              <SegmentsPreviewStep
                videoUrl={previewUrl}
                durationSeconds={duration}
                onCancel={() => setStep('trim')}
                onPublish={publishSegments}
                busy={false}
                publishPhase={null}
                uploadProgressPct={0}
              />
            </motion.div>
          )}
        </AnimatePresence>
    </DialogContent>
  );
};

export default StoryUploadDialog;
